// XAuthCallback — handles the OAuth 2.0 redirect from X for BOTH flows:
//
//   • CONNECT flow (Producer linking X for Follow & Earn) — runs inside
//     a popup opened by XFollowPanel. We postMessage the {code, state}
//     payload back to the opener and close the popup. The actual token
//     exchange happens in the parent's authenticated context via
//     /api/x/connect/callback.
//
//   • SIGN-IN flow (anonymous-to-authed via the X funnel) — runs in
//     the SAME tab (full-page redirect). We POST /api/x/signin/callback
//     ourselves, persist the returned JWT, then navigate to "/" (or
//     to /claim-username if the backend signals pending_username=true
//     for a brand-new account).
//
// We distinguish the two by `localStorage.ge.x_signin_intent`. The
// SIGN-IN button sets it to "signin" right before the redirect.
// CONNECT clears it via postMessage so a stale flag on the next visit
// can't accidentally trigger sign-in.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const XAuthCallback = () => {
  const navigate = useNavigate();
  const { setSessionFromX } = useAuth();
  const [status, setStatus] = useState("Finishing X sign-in…");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const errorParam = params.get("error_description") || params.get("error");

    const intent = localStorage.getItem("ge.x_signin_intent") || "connect";

    // ─────── SIGN-IN INTENT (anonymous → authed) ───────
    if (intent === "signin") {
      // Clear the intent immediately so a refresh / back-button doesn't
      // re-execute the callback against a now-consumed state.
      localStorage.removeItem("ge.x_signin_intent");

      if (errorParam || !code || !state) {
        setStatus(`Sign-in cancelled${errorParam ? `: ${errorParam}` : ""}.`);
        const t = setTimeout(() => navigate("/", { replace: true }), 1500);
        return () => clearTimeout(t);
      }

      const expectedState = localStorage.getItem("ge.x_signin_state") || "";
      if (expectedState && expectedState !== state) {
        // CSRF guard — the state we sent doesn't match the one we got
        // back. Reject and force the user to restart.
        setStatus("Session mismatch — please try signing in again.");
        const t = setTimeout(() => navigate("/", { replace: true }), 2000);
        return () => clearTimeout(t);
      }
      localStorage.removeItem("ge.x_signin_state");

      const inflight = (() => {
        try { return JSON.parse(localStorage.getItem("ge.x_signin_inflight") || "{}"); }
        catch { return {}; }
      })();
      localStorage.removeItem("ge.x_signin_inflight");

      (async () => {
        try {
          const res = await api.post("/x/signin/callback", {
            code, state,
            utm_source: inflight.utm_source || undefined,
            utm_campaign: inflight.utm_campaign || undefined,
            list_slug: inflight.list_slug || undefined,
            attribution_id: inflight.attribution_id || undefined,
          });
          const token = res.data?.access_token;
          const user = res.data?.user;
          if (!token || !user) throw new Error("malformed callback response");
          // Adopt the new session via AuthContext — it persists the
          // JWT to localStorage internally so a hard refresh rehydrates.
          if (typeof setSessionFromX === "function") {
            setSessionFromX(res.data);
          }
          // After a successful sign-in, attempt the follower import
          // OPPORTUNISTICALLY — non-blocking, safe to fire even on
          // returning sign-ins (the endpoint is idempotent).
          api.post("/x/signin/import-following", {}).catch(() => {});
          // First-time accounts go to the claim-handle screen; returning
          // accounts land on the feed they came for.
          if (res.data?.pending_username) {
            navigate(`/claim-username?x_handle=${encodeURIComponent(res.data?.x_handle || user.username)}`, { replace: true });
          } else {
            navigate("/", { replace: true });
          }
        } catch (err) {
          console.error("X sign-in callback failed", err);
          const msg = err?.response?.data?.detail || err?.message || "Sign-in failed";
          setStatus(msg);
          const t = setTimeout(() => navigate("/", { replace: true }), 2500);
          return () => clearTimeout(t);
        }
      })();
      return undefined;
    }

    // ─────── CONNECT INTENT (popup → postMessage to opener) ───────
    const payload = {
      type: "x_oauth_callback",
      code, state,
      error: errorParam,
    };
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(payload, window.location.origin);
      }
    } catch (e) {
      // Cross-origin opener — falls back to the redirect path below.
      // Log to Sentry so we can spot a sudden uptick (would indicate
      // the OAuth flow is being initiated from an unexpected origin).
      try {
        if (typeof window !== "undefined" && window.Sentry) {
          window.Sentry.captureException(e, {
            tags: { area: "x_auth_callback_postmessage" },
          });
        }
      } catch (_e) { /* Sentry unavailable */ }
    }
    const t = setTimeout(() => {
      try { window.close(); } catch (e) {
        // Popup was opened non-script — `window.close()` is a no-op.
        // Expected; nothing to surface.
        void e;
      }
      if (!window.closed) window.location.replace("/dashboard?x_prompt=1");
    }, 250);
    return () => clearTimeout(t);
  }, [navigate, setSessionFromX]);

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center text-gray-300 p-6">
      <div className="text-center">
        <p className="text-lg font-medium">{status}</p>
        <p className="text-sm text-gray-500 mt-1">You can close this window.</p>
      </div>
    </div>
  );
};

export default XAuthCallback;
